import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money, dateFmt, formatApiError, API_BASE } from "@/lib/api";
import { Calculator, Plus, CheckCircle2, XCircle, FileText, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_STYLES = {
    pendiente: "fm-badge-warning",
    ganada: "fm-badge-positive",
    perdida: "fm-badge-critical",
    vencida: "fm-badge-neutral",
};

export default function Quotes() {
    const { user } = useAuth();
    const isInternal = user?.role !== "cliente";
    const [list, setList] = useState([]);

    const load = async () => {
        try {
            const r = await api.get("/quotes");
            if (r?.data) setList(r.data);
        } catch (e) { console.warn(e); }
    };
    useEffect(() => { load(); }, []);

    const setStatus = async (id, status) => {
        try {
            await api.patch(`/quotes/${id}/status`, { status });
            toast.success(`Marcada como ${status}`);
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    return (
        <div className="space-y-4" data-testid="quotes-page">
            <div className="flex justify-between items-center">
                <div className="text-sm text-slate-500">{list.length} cotización(es)</div>
                <Link to="/quoter" className="fm-btn-accent">
                    <Plus className="w-3.5 h-3.5" /> Nueva cotización
                </Link>
            </div>

            <div className="fm-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                            {["Folio", "Cliente", "Total", "Estatus", "Fecha", "Acciones"].map((h) => (
                                <th key={h} className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((q) => (
                            <tr key={q.id} className="border-b border-slate-100 hover:bg-slate-50">
                                <td className="py-2 px-3 font-mono text-xs font-bold">{q.folio}</td>
                                <td className="py-2 px-3">{q.client_name}</td>
                                <td className="py-2 px-3 text-right font-mono font-bold tabular-nums">{money(q.total)}</td>
                                <td className="py-2 px-3"><span className={`fm-badge ${STATUS_STYLES[q.status] || "fm-badge-neutral"}`}>{q.status}</span></td>
                                <td className="py-2 px-3 text-xs font-mono text-slate-500">{dateFmt(q.created_at)}</td>
                                <td className="py-2 px-3">
                                    <div className="flex items-center gap-2">
                                        <a
                                            href={`${API_BASE}/pdf/quote/${q.id}?mode=comercial`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 rounded-sm"
                                            title="PDF Comercial · listo para compartir con el cliente"
                                            data-testid={`quote-pdf-comercial-${q.folio}`}
                                        >
                                            <FileText className="w-3 h-3" /> Comercial
                                        </a>
                                        {isInternal && (
                                            <a
                                                href={`${API_BASE}/pdf/quote/${q.id}?mode=interno`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 border border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100 rounded-sm"
                                                title="PDF Interno · uso exclusivo de cabina / comercial (incluye reglas, autorizaciones, metadatos)"
                                                data-testid={`quote-pdf-interno-${q.folio}`}
                                            >
                                                <ShieldAlert className="w-3 h-3" /> Interno
                                            </a>
                                        )}
                                        {q.status === "pendiente" && (
                                            <>
                                                <button onClick={() => setStatus(q.id, "ganada")} className="text-emerald-600 hover:text-emerald-800" title="Marcar como ganada">
                                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                                </button>
                                                <button onClick={() => setStatus(q.id, "perdida")} className="text-red-500 hover:text-red-700" title="Marcar como perdida">
                                                    <XCircle className="w-3.5 h-3.5" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {list.length === 0 && (
                    <div className="p-10 text-center text-sm text-slate-500">
                        <Calculator className="w-10 h-10 mx-auto mb-2 text-slate-300" strokeWidth={1} />
                        Sin cotizaciones. <Link to="/quoter" className="text-red-600 font-bold underline">Crear la primera</Link>
                    </div>
                )}
            </div>
        </div>
    );
}
